import type { User } from "@supabase/supabase-js";
import { assertSupabaseConfigured, supabase } from "../lib/supabase";
import type { Profile } from "../types/tenant";

function metadataString(user: User, key: string) {
  const value = user.user_metadata?.[key];
  return typeof value === "string" ? value.trim() : "";
}

export const profileService = {
  async getCurrentProfile(userId: string): Promise<Profile | null> {
    assertSupabaseConfigured();

    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return data;
  },

  async ensureProfile(user: User): Promise<Profile> {
    assertSupabaseConfigured();

    const existing = await this.getCurrentProfile(user.id);

    if (existing) {
      if (user.email && existing.email !== user.email) {
        const { data, error } = await supabase
          .from("profiles")
          .update({ email: user.email })
          .eq("id", user.id)
          .select("*")
          .single();

        if (error) {
          throw error;
        }

        return data;
      }

      return existing;
    }

    const { data, error } = await supabase
      .from("profiles")
      .insert({
        id: user.id,
        email: user.email ?? "",
        full_name: metadataString(user, "full_name"),
        avatar_url: metadataString(user, "avatar_url") || null,
      })
      .select("*")
      .single();

    if (error) {
      throw error;
    }

    return data;
  },
};
